import React, { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  LayoutGrid,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Presentation,
  Grid2X2,
  Square,
  Eye,
  Magnet,
  Loader2,
  FileText,
  Cable,
  Link,
} from 'lucide-react';
import { AIToolsMenu } from './AIToolsMenu';
import { PortalioButton } from './PortalioButton';
import { PortalOutboundPreviewDialog } from './PortalOutboundPreviewDialog';
import { AnnotationToolbar } from './AnnotationToolbar';
import { useAppStore } from '@/stores/appStore';
import { useFloorPlanContext } from '../FloorPlanProvider';
import { isPortalOutboundExportEnabled } from '@/utils/portalOutboundFeature';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface FloorPlanToolbarProps {
  activeTab: 'floorPlan' | 'loads' | 'reports';
  onTabChange: (tab: 'floorPlan' | 'loads' | 'reports') => void;
}

export function FloorPlanToolbar({ activeTab, onTabChange }: FloorPlanToolbarProps) {
  // Get data from context
  const { data, floorPlan } = useFloorPlanContext();
  const { circuits, controls } = data;
  const portalOutboundEnabled = isPortalOutboundExportEnabled();

  // Show loads tab only if there are circuits or controls
  const showLoadsTab = circuits.length > 0 || controls.length > 0;
  const loadsCount = circuits.length;
  // Get store actions directly
  const updateViewport = useAppStore(state => state.updateViewport);
  const setPresentationMode = useAppStore(state => state.setPresentationMode);
  const setActiveDrawer = useAppStore(state => state.setActiveDrawer);

  // Get state values separately
  const viewport = useAppStore(state => state.floorPlan.viewport);
  const roomsVisible = useAppStore(state => state.rooms.roomsVisible);
  const toggleRoomVisibility = useAppStore(state => state.toggleRoomVisibility);
  const showDimensions = useAppStore(state => state.rooms.showDimensions);
  const toggleRoomDimensions = useAppStore(state => state.toggleRoomDimensions);
  const snapEnabled = useAppStore(state => state.alignment.snapEnabled);
  const toggleSnapEnabled = useAppStore(state => state.toggleSnapEnabled);
  const isProcessing = useAppStore(state => state.aiTools.isProcessing);
  const isProcessingRooms = useAppStore(state => state.aiTools.isProcessingRooms);
  const cablePathsVisible = useAppStore(state => state.cablePaths.visible);
  const toggleCablePathVisibility = useAppStore(state => state.toggleCablePathVisibility);
  const loadLinesVisible = useAppStore(state => state.loadLines.visible);
  const toggleLoadLineVisibility = useAppStore(state => state.toggleLoadLineVisibility);
  const loadLineThickness = useAppStore(state => state.loadLines.thickness);
  const setLoadLineThickness = useAppStore(state => state.setLoadLineThickness);

  // Smooth animation function
  const animateViewport = useCallback(
    (target: { scale: number; positionX: number; positionY: number }, duration: number) => {
      const startTime = performance.now();
      const startViewport = useAppStore.getState().floorPlan.viewport;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Cubic ease-in-out
        const easeProgress =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;

        const scale = startViewport.scale + (target.scale - startViewport.scale) * easeProgress;
        const positionX =
          startViewport.positionX + (target.positionX - startViewport.positionX) * easeProgress;
        const positionY =
          startViewport.positionY + (target.positionY - startViewport.positionY) * easeProgress;

        updateViewport({ scale, positionX, positionY });

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    },
    [updateViewport],
  );

  // Zoom controls using smooth animations
  const handleZoomIn = useCallback(() => {
    const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
    if (!container) return;

    const state = useAppStore.getState();
    const currentViewport = state.floorPlan.viewport;
    const currentScale = currentViewport.scale;
    const newScale = Math.min(currentScale * 1.2, 10);

    // Calculate center of viewport
    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    // Calculate the point in image space that's at the center of the viewport
    const imageCenterX = (centerX - currentViewport.positionX) / currentScale;
    const imageCenterY = (centerY - currentViewport.positionY) / currentScale;

    // Calculate new position to keep the same point at the center
    const newPositionX = centerX - imageCenterX * newScale;
    const newPositionY = centerY - imageCenterY * newScale;

    animateViewport(
      {
        scale: newScale,
        positionX: newPositionX,
        positionY: newPositionY,
      },
      200,
    ); // 200ms animation
  }, [animateViewport]);

  const handleZoomOut = useCallback(() => {
    const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
    if (!container) return;

    const state = useAppStore.getState();
    const currentViewport = state.floorPlan.viewport;
    const currentScale = currentViewport.scale;
    const newScale = Math.max(currentScale * 0.8, 0.1);

    // Calculate center of viewport
    const containerRect = container.getBoundingClientRect();
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;

    // Calculate the point in image space that's at the center of the viewport
    const imageCenterX = (centerX - currentViewport.positionX) / currentScale;
    const imageCenterY = (centerY - currentViewport.positionY) / currentScale;

    // Calculate new position to keep the same point at the center
    const newPositionX = centerX - imageCenterX * newScale;
    const newPositionY = centerY - imageCenterY * newScale;

    animateViewport(
      {
        scale: newScale,
        positionX: newPositionX,
        positionY: newPositionY,
      },
      200,
    ); // 200ms animation
  }, [animateViewport]);

  const handleFitToScreen = useCallback(() => {
    const container = document.querySelector('.floor-plan-viewport') as HTMLElement;
    const imageSize = useAppStore.getState().floorPlan.imageSize;
    if (!container || !imageSize.width || !imageSize.height) return;

    const containerRect = container.getBoundingClientRect();
    const scaleX = containerRect.width / imageSize.width;
    const scaleY = containerRect.height / imageSize.height;
    const scale = Math.min(scaleX, scaleY) * 0.9;

    const positionX = (containerRect.width - imageSize.width * scale) / 2;
    const positionY = (containerRect.height - imageSize.height * scale) / 2;

    animateViewport({ scale, positionX, positionY }, 300); // 300ms animation for fit to screen
  }, [animateViewport]);

  const handleOpenPresentationMode = useCallback(() => {
    setPresentationMode(true);
    setActiveDrawer('presentation');
  }, [setPresentationMode, setActiveDrawer]);

  // Keyboard shortcuts removed per user request

  const currentZoom = Math.round(viewport.scale * 100);

  return (
    <div className="bg-white/95 border-b border-slate-200/80 px-4 py-2 shadow-[0_1px_0_rgba(15,23,42,0.04)] backdrop-blur">
      <div className="flex items-center justify-between gap-4 overflow-x-auto overflow-y-hidden">
        {/* Left section - Tabs and Tools */}
        <div className="flex items-center gap-4">
          {/* Floor Plan/Loads/Reports Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={value => onTabChange?.(value as 'floorPlan' | 'loads' | 'reports')}
          >
            <TabsList className="h-9">
              <TabsTrigger value="floorPlan" className="text-xs">
                <LayoutGrid className="h-4 w-4 mr-2" />
                Floor Plan
              </TabsTrigger>
              {showLoadsTab && (
                <TabsTrigger value="loads" className="text-xs">
                  Loads ({loadsCount})
                </TabsTrigger>
              )}
              <TabsTrigger value="reports" className="text-xs">
                <FileText className="h-4 w-4 mr-2" />
                Reports
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <Separator orientation="vertical" className="h-6" />
          {/* Zoom Controls */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>

            <div className="min-w-[60px] text-center text-xs font-medium text-muted-foreground">
              {currentZoom}%
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleZoomIn}
              title="Zoom In"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={handleFitToScreen}
              title="Fit to Screen"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
          <Separator orientation="vertical" className="h-6" />
          {/* Annotation Tools */}
          <AnnotationToolbar />
          <Separator orientation="vertical" className="h-6" />
          {/* Snap Toggle */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={snapEnabled ? 'default' : 'ghost'}
                  size="sm"
                  onClick={toggleSnapEnabled}
                  className="h-8 w-8 p-0"
                >
                  <Magnet className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{snapEnabled ? 'Snap Enabled' : 'Snap Disabled'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Center section - AI Detection Status */}
        {(isProcessing || isProcessingRooms) && (
          <Badge
            variant="secondary"
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary border-primary/20',
              'animate-in fade-in slide-in-from-top-2 duration-300',
            )}
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span className="text-sm font-medium">
              {isProcessing && isProcessingRooms
                ? 'Running AI detection...'
                : isProcessing
                  ? 'Detecting lighting...'
                  : 'Detecting rooms...'}
            </span>
          </Badge>
        )}

        {/* Right section - View Options and Actions */}
        <div className="flex items-center gap-2">
          {/* View Options Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <Eye className="h-4 w-4 mr-1" />
                <span className="text-xs">View</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <DropdownMenuLabel>View Options</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={roomsVisible}
                onCheckedChange={toggleRoomVisibility}
              >
                <Grid2X2 className="h-4 w-4 mr-2" />
                Show Rooms
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={showDimensions}
                onCheckedChange={toggleRoomDimensions}
              >
                <Square className="h-4 w-4 mr-2" />
                Show Dimensions
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={cablePathsVisible}
                onCheckedChange={toggleCablePathVisibility}
              >
                <Cable className="h-4 w-4 mr-2" />
                Show Cable Paths
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={loadLinesVisible}
                onCheckedChange={toggleLoadLineVisibility}
              >
                <Link className="h-4 w-4 mr-2" />
                Show Load Lines
              </DropdownMenuCheckboxItem>
              {loadLinesVisible && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    Load Line Thickness
                  </DropdownMenuLabel>
                  <div className="flex items-center gap-1 px-2 pb-2">
                    {[
                      { value: 1, label: 'Thin' },
                      { value: 2, label: 'Medium' },
                      { value: 3, label: 'Thick' },
                    ].map(opt => (
                      <Button
                        key={opt.value}
                        variant={loadLineThickness === opt.value ? 'default' : 'outline'}
                        size="sm"
                        className="h-6 text-xs flex-1"
                        onClick={() => setLoadLineThickness(opt.value)}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Separator orientation="vertical" className="h-6" />

          {/* Portal.io Integration Button - Standalone as requested */}
          <PortalioButton floorPlanId={floorPlan.id} />
          {portalOutboundEnabled && <PortalOutboundPreviewDialog floorPlanId={floorPlan.id} />}

          {/* AI Tools Menu - Keep as separate dropdown */}
          <AIToolsMenu />

          {/* Presentation Mode Button */}
          <TooltipProvider delayDuration={500}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenPresentationMode}
                  className="h-8 w-8 p-0"
                >
                  <Presentation className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Presentation Mode</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
}
